import PopupHeader from "@builder/components/app/Popup/PopupHeader";
import "./style.scss";
import { ActionBuilder, BaseLayer, Build, BuildNode, TemplateDiv } from "@common/build/types";
import React, { useEffect, useRef, useState } from "react";
import { getTemplates } from "@builder/utils/mongoFunctions";
import Loading from "@builder/components/app/Loading";
import AdvSelectInput from "@builder/components/app/Settings/StoreData/AdvSelectInput";
import OliverText from "@builder/components/general/OliverText";
import { componentWrapperMap } from "@builder/components/app/OliverBuildFrame/map";
import { ActiveEntityType } from "@common/build/activeEntityType";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { useTranslation } from "react-i18next";
import { copyTemplateNode, deleteNode, selectNode } from "@builder/store/slices/BuilderSlice/builderSlice";
import { closePopup } from "@builder/store/slices/popupSlice";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
// import TextInput from "@builder/components/general/TextInput";

const getTemplateDivs = (build: Build, filter?: string | null): TemplateWrapper[] => {
    const tDivs: TemplateWrapper[] = [];

    build.pages.forEach((page, index) => {
        Object.values(page.components).forEach(node => {
            if (node.name === "templateDiv") {
                tDivs.push({ node, layer: { type: "pages", index } });
            }
        });
    });

    build.popups.forEach((popup, index) => {
        Object.values(popup.components).forEach(node => {
            if (node.name === "templateDiv") {
                tDivs.push({ node, layer: { type: "popups", index } });
            }
        });
    });

    if (filter) {
        return tDivs.filter(tDiv => tDiv.node.tag?.toLowerCase().includes(filter.toLowerCase()));
    }
    return tDivs;
};

type TemplateWrapper = { node: TemplateDiv; layer: { type: "popups" | "pages"; index: number } };

const TemplateLibraryPopup: React.FC = () => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(true);
    const [templateDivsLoading, setTemplateDivsLoading] = useState<boolean>(true);
    const [currentTemplatePage, setCurrentTemplatePage] = useState<Build | null>(null);
    const [templateDivs, setTemplateDivs] = useState<TemplateWrapper[]>([]);
    const [templateIndex, setTemplateIndex] = useState<number>(0);
    const templateDivRefs = useRef<(HTMLDivElement | null)[]>([]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const storeData = useAppSelector(state => state.storeData.data);
    const { t } = useTranslation();
    const templateLibraryFilter = useAppSelector(state => state.popup.templateLibraryFilter);
    const [pageMap, setPageMap] = useState<{ build: Build; templates: TemplateWrapper[] }[]>([]);
    const selectedNode = useAppSelector(getSelectedNode);

    const getDynamicDataObject = (node: BuildNode) => {
        if (node.dynamicData) {
            const dType = node.dynamicData.split(".")[0];
            switch (dType) {
                case ActiveEntityType.COMPANY:
                    return storeData?.company;
                case ActiveEntityType.PRODUCT:
                    return storeData?.product;
                case ActiveEntityType.ORDER:
                    return storeData?.order;
                case ActiveEntityType.CUSTOMER:
                    return storeData?.customer;
            }
        }
    };

    const renderNodeNoFunction = (layer: BaseLayer, key: string): React.ReactNode => {
        if (layer.components[key]) {
            const node = JSON.parse(JSON.stringify(layer.components[key])) as BuildNode;

            if (node) {
                const dynamicDataObject = getDynamicDataObject(node);

                const Component = componentWrapperMap[node.name as keyof typeof componentWrapperMap];

                if (node.name === "templateDiv" && node.attr?.style && scrollRef.current) {
                    let width = node.attr.style.width;
                    if (typeof width === "string" && !width.includes("%")) {
                        width = parseInt(width.replace(/\D/g, ""));
                    }

                    let scale = 1;

                    if (typeof width === "number" && !Number.isNaN(width)) {
                        scale = scrollRef.current.clientWidth / width;
                    }

                    if (scale < 1) {
                        node.attr.style.transform = `scale(${scale})`;
                    }
                }

                return (
                    <Component
                        node={node}
                        key={key}
                        t={t}
                        builderProps={{
                            containerDisabled: true
                        }}
                        dataObject={dynamicDataObject}
                    >
                        {node.children?.map(node => renderNodeNoFunction(layer, node))}
                    </Component>
                );
            }
        }
    };

    useEffect(() => {
        const fetchTemplates = async () => {
            setLoading(true);

            const fetchedTemplates = await getTemplates();
            const newPageMap = fetchedTemplates
                .map(build => {
                    const templates = getTemplateDivs(build, templateLibraryFilter);
                    return { build, templates };
                })
                .filter(page => page.templates.length > 0);

            setPageMap(newPageMap);

            if (newPageMap.length > 0) {
                setCurrentTemplatePage(newPageMap[0].build);
            } else {
                setCurrentTemplatePage(null);
            }

            setLoading(false);
        };

        fetchTemplates();
    }, [templateLibraryFilter]);

    useEffect(() => {
        if (currentTemplatePage && pageMap.length > 0) {
            setTemplateDivsLoading(true);
            const tDivs = pageMap.find(p => p.build.id === currentTemplatePage.id)?.templates || [];
            setTemplateDivs(tDivs);
            setTemplateIndex(0);
            setTemplateDivsLoading(false);
        } else {
            setTemplateDivs([]);
        }
    }, [currentTemplatePage, pageMap]);

    useEffect(() => {
        templateDivRefs.current = templateDivs.map((_, i) => templateDivRefs.current[i] || null);
        setTemplateIndex(0);
    }, [templateDivs]);

    const updateCurrentTemplatePage = (value: Build) => {
        setCurrentTemplatePage(value);
    };

    const handleScroll = () => {
        if (scrollRef.current && templateDivRefs.current.length > 0) {
            const scrollTop = scrollRef.current.scrollTop;
            const scrollHeight = scrollRef.current.scrollHeight;
            const clientHeight = scrollRef.current.clientHeight;
            const buffer = 50; // Adjust this value to control the buffer room

            let cumulativeHeight = 0;
            let index = 0;

            // Check if the user is near the bottom of the scrollable area
            if (scrollTop + clientHeight >= scrollHeight - buffer) {
                // If at the bottom, set index to the last template div
                setTemplateIndex(templateDivRefs.current.length - 1);
                return;
            }

            // Otherwise, find the appropriate index based on scroll position
            for (let i = 0; i < templateDivRefs.current.length; i++) {
                const ref = templateDivRefs.current[i];
                if (ref) {
                    const divHeight = ref.offsetHeight;
                    cumulativeHeight += divHeight;

                    // If the scrollTop is within this div, considering the buffer, set the index
                    if (scrollTop + buffer < cumulativeHeight) {
                        index = i;
                        break;
                    }
                }
            }

            setTemplateIndex(index);
        }
    };

    const handleSidebarClick = (index: number) => {
        if (scrollRef.current) {
            let scrollHeight = 0;
            for (let i = 0; i < index; i++) {
                scrollHeight += templateDivRefs.current[i]?.clientHeight || 0;
            }
            scrollRef.current.scrollTo({ top: scrollHeight, behavior: "smooth" });
        }
    };

    const getAction = (templateDiv: TemplateWrapper): ActionBuilder | undefined => {
        const actionIds = templateDiv.node.actionsBuilder;
        if (actionIds?.[0] && currentTemplatePage?.actionBuilder?.[actionIds[0]]) {
            return currentTemplatePage.actionBuilder[actionIds[0]];
        }
        return undefined;
    };

    const copyTemplateAndReplace = (templateDiv: TemplateWrapper) => {
        const layer = currentTemplatePage?.[templateDiv.layer.type]?.[templateDiv.layer.index];
        const action = getAction(templateDiv);
        if (layer) {
            if (selectedNode) {
                const nodeParent = selectedNode.parent;
                dispatch(deleteNode(selectedNode.id));
                if (nodeParent) {
                    dispatch(selectNode(nodeParent));
                }
            }

            dispatch(copyTemplateNode({ layer, node: templateDiv.node, action }));
            dispatch(closePopup());
        }
    };

    return (
        <div className="template-library">
            <PopupHeader src="/book-open.svg" title="Design Set Library">
                {/* <div className="search-wrapper">
                    <TextInput
                        value={templateSearchValue}
                        placeholder={"Search for templates"}
                        callback={searchCallback}
                        icon={{ src: "/search-icon.svg" }}
                    />
                </div> */}
            </PopupHeader>
            <div className="template-library-body">
                {loading || !currentTemplatePage ? (
                    <Loading />
                ) : (
                    <>
                        <div className="left-column">
                            <AdvSelectInput
                                value={currentTemplatePage}
                                options={pageMap.map(page => page.build)}
                                inputValue=""
                                label="Design sets"
                                hasMore={false}
                                findLabel={option => option?.name || ""}
                                clickCallback={updateCurrentTemplatePage}
                                scrollCallback={() => {}}
                                searchCallback={() => {}}
                                disabled={templateDivsLoading || !currentTemplatePage}
                            />

                            {templateDivs.map((tDiv, index) => (
                                <button
                                    className={templateIndex === index ? "current" : ""}
                                    key={tDiv.node.id}
                                    onClick={() => handleSidebarClick(index)}
                                >
                                    {tDiv.node.customName || `Template #${index + 1}`}
                                </button>
                            ))}
                        </div>
                        <div className="right-column" ref={scrollRef} onScroll={handleScroll}>
                            {templateDivs.map((tDiv, index) => {
                                if (currentTemplatePage && currentTemplatePage[tDiv.layer.type][tDiv.layer.index]) {
                                    return (
                                        <div className="template-div" key={tDiv.node.id} ref={e => (templateDivRefs.current[index] = e)}>
                                            <div className="t-d-header">
                                                <OliverText text={tDiv.node.customName || `Template #${index + 1}`} size="l" weight="semi-bold" />
                                                <button onClick={() => copyTemplateAndReplace(tDiv)}>
                                                    <img src="/copy-icon.svg" alt="" />
                                                    {t("generalComponents.button.Copy to page")}
                                                </button>
                                            </div>
                                            <div className="t-d-render" style={{ fontFamily: "poppins" }}>
                                                {tDiv.node?.previewImage ? (
                                                    <img className="template-preview-image" src={tDiv.node.previewImage} />
                                                ) : (
                                                    renderNodeNoFunction(currentTemplatePage[tDiv.layer.type][tDiv.layer.index], tDiv.node.id)
                                                )}
                                            </div>
                                        </div>
                                    );
                                } else {
                                    return null;
                                }
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TemplateLibraryPopup;
